import { notifications } from "@mantine/notifications"
import {
  UseMutationOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query"
import { AxiosError } from "axios"

import { AdminRequests, AdminResponses } from "@tfg/types/endpoints/admin"
import { CustomFieldDefinitionSyncToProductsParamsValidationType } from "@tfg/types/schemas/customField"
import { PaginationType } from "@tfg/types/schemas/utils"

import { httpClient } from "~/backend/axios"
import { queryClient } from "~/backend/queryClient"
import { showAxiosError } from "~/utils/showAxiosError"

export const useListCustomFieldsQuery = (orgId?: number) =>
  useQuery(["customFields", orgId], async () => {
    const res = await httpClient.get<AdminResponses.CustomFieldDefinition[]>(
      `/admin/custom-fields`,
      {
        params: {
          orgId,
        },
      }
    )
    return res.data
  })

export const useCreateCustomFieldMutation = (
  options?: UseMutationOptions<
    unknown,
    AxiosError,
    AdminRequests.CustomFieldDefinitionCreate
  >
) =>
  useMutation({
    mutationFn: async (
      customField: AdminRequests.CustomFieldDefinitionCreate
    ) => {
      return await httpClient.post(`/admin/custom-fields`, customField)
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)

      notifications.show({
        title: "Success",
        message: "Custom field definition created",
        color: "green",
      })

      queryClient.invalidateQueries(["customFields"])
    },
    onError: (error: AxiosError) => {
      notifications.show({
        title: "Error",
        message: error.message,
        color: "red",
      })
    },
  })

export const useDeleteCustomFieldMutation = (
  options?: UseMutationOptions<void, AxiosError, unknown>
) => {
  return useMutation({
    ...options,
    mutationFn: async (customFieldId) => {
      return httpClient.delete(`/admin/custom-fields/${customFieldId}`)
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
      queryClient.invalidateQueries(["customFields"])
    },
  })
}

export const useListTextContentsQuery = ({
  pagination,
  searchParams,
}: PaginationType) =>
  useQuery(["textContents", searchParams], async () => {
    const res = await httpClient.get<AdminResponses.TextContent[]>(
      "/admin/text-contents",
      {
        params: {
          pagination,
          searchParams,
        },
      }
    )
    return res.data
  })

export const useCreateTextContentMutation = (
  options?: UseMutationOptions<
    AdminResponses.TextContentWithouTranslation,
    unknown,
    { originalText: string }
  >
) => {
  return useMutation({
    ...options,
    mutationFn: async ({ originalText }) => {
      const res = await httpClient.post("/admin/text-contents", {
        originalText,
      })
      return res.data
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
      queryClient.invalidateQueries(["textContents"])
      notifications.show({
        title: "Added",
        message: "Successfully created text content",
        color: "green",
      })
    },
  })
}

export const useSyncCustomFieldsToProductsMutation = (
  params: CustomFieldDefinitionSyncToProductsParamsValidationType,
  options?: UseMutationOptions<unknown, unknown, unknown>
) =>
  useMutation({
    mutationFn: async () => {
      const res =
        await httpClient.get<AdminRequests.CustomFieldDefinitionCreate>(
          `/admin/custom-fields/sync-to-products`,
          {
            params,
          }
        )
      return res.data
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
      queryClient.invalidateQueries(["customFields"])
    },
  })

export const useUpdateCustomFieldMutation = (
  { id }: { id: number | undefined },
  options?: UseMutationOptions<
    unknown,
    AxiosError,
    AdminRequests.CustomFieldDefinitionCreate
  >
) =>
  useMutation({
    mutationFn: async (
      customField: AdminRequests.CustomFieldDefinitionCreate
    ) => {
      if (!id) {
        throw new Error("Custom field id is required")
      }

      return await httpClient.put(`/admin/custom-fields/${id}`, customField)
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
      notifications.show({
        title: "Success",
        message: "Custom field definition updated",
        color: "green",
      })
      queryClient.invalidateQueries(["customFields"])
    },
    onError: (error: AxiosError) => {
      showAxiosError({
        error,
        showNotification: true,
        notificationTitle: "Error",
        defaultMessage: "Error updating custom field",
      })
    },
  })
